// TODO: Task 904718: refactor to use explicit dependency for es6 syntax rather than global variable
/* global Set */

import _ from 'underscore';
import { BaseView } from '@bingads-webui/base-view';
import type { DeterministicViewInstance } from './deterministic-view';

/**
 * @class DeterministicViewEventEmitter
 * @extends Backbone.Events
 */

/**
 * The event emitter inherit from BaseView, it's indeed the same object as
 * BaseView.eventEmitter
 * @memberof! DeterministicView
 * @type DeterministicViewEventEmitter
 */
const { eventEmitter } = BaseView;

function replaceElement(elNew: Node, elOld: Node | undefined) {
  if (elOld && elOld.parentNode) {
    elOld.parentNode.replaceChild(elNew, elOld);
  }
}

function removeElement(el: Node | null) {
  // ChildNode.remove() is not supported in IE
  // see: https://developer.mozilla.org/en-US/docs/Web/API/ChildNode/remove
  if (el && el.parentNode) {
    el.parentNode.removeChild(el);
  }
}

/**
 * DeterministicView render engine. It determines how a view is rendered, and
 * how the children views are aggregated with their parent.
 */
export class RenderEngine {
  animationFrame: number | null;
  renderQueue: Set<DeterministicViewInstance> | null;
  constructor() {
    this.animationFrame = null;
    this.renderQueue = new Set();
  }

  /**
   * Render a view
   * @param {DeterministicView} view - The view to be rendered
   * @returns {void}
   */
  renderView(view: DeterministicViewInstance) {
    /**
     * A view is going to be rendered
     * @event DeterministicViewEventEmitter#willRenderView
     * @type {DeterministicView}
     */
    eventEmitter.trigger('willRenderView', view);

    /**
     * Warn about potential bug
     * @event DeterministicViewEventEmitter#warning
     * @type {Error}
     */
    if (!BaseView.getParent(view) && !view.el.parentNode) {
      // All root `DeterministicView`s should have their `el` attached to DOM
      // tree. Otherwise, it could be an orphan view which never be attached.
      eventEmitter.trigger(
        'warning',
        _.extend(new Error('Unexpected orphan view'), { view })
      );
    }

    // Detach all children views, to protect the child views from being damaged
    // by the DeterministicView#renderTrunk() method
    const elTemp = document.createElement('DIV');
    view.$el.children().appendTo(elTemp);

    // Render the view
    view.renderTrunk();

    // Index the placeholders
    const placeholders: Record<string, HTMLElement> = {};

    view.$('[data-ref]').each((_index: number, el: HTMLElement) => {
      const ref = el.dataset.ref as string;
      placeholders[ref] = el;
      if (!BaseView.getChild(view, ref)) {
        // Found a placeholder cannot be fulfilled with a child view. It usually
        // indicates a typo in the template
        eventEmitter.trigger(
          'warning',
          _.extend(new Error('Dangling placeholder'), { view, ref })
        );
      }
    });

    // Attach the child views which have placeholders rendered
    // Remove the child views without placeholder
    _.each(BaseView.getChildrenRefs(view), (ref: string) => {
      const child = BaseView.getChild(view, ref) as DeterministicViewInstance;

      if (_.has(placeholders, ref)) {
        replaceElement(child.el, placeholders[ref]);
      } else {
        child.remove();
      }
    });

    removeElement(elTemp);

    /**
     * A view has been completely rendered
     * @event DeterministicViewEventEmitter#willRenderView
     * @type {DeterministicView}
     */
    eventEmitter.trigger('didRenderView', view);

    /**
     * The view has been completely rendered
     * @event DeterministicView#rendered
     */
    view.trigger('rendered');
  }

  /**
   * Render an animation frame
   * @private
   * @returns {void}
   */
  renderFrame() {
    const frame = this.animationFrame;
    const views = this.renderQueue as Set<DeterministicViewInstance>;

    this.animationFrame = null;
    this.renderQueue = null;

    /**
     * A new animation frame will be rendered
     * @event DeterministicViewEventEmitter#willRenderFrame
     * @type {number}
     */
    eventEmitter.trigger('willRenderFrame', frame);

    views.forEach((view: DeterministicViewInstance) =>
      BaseView.isValid(view) && this.renderView(view));

    /**
     * An animation frame has been rendered
     * @event DeterministicViewEventEmitter#didRenderFrame
     * @type {number}
     */
    eventEmitter.trigger('didRenderFrame', frame);
  }

  /**
   * Schedule rendering a view in next animation frame
   * @param {DeterministicView} view - The view to render
   * @returns {void}
   */
  scheduleRenderView(view: DeterministicViewInstance) {
    if (view.options && view.options.renderInSync) {
      // Allow some views render without requestAnimationFrame,
      // so that they can be rendered in the background tab.
      this.renderView(view);
      return;
    }

    if (!this.renderQueue) {
      this.renderQueue = new Set();
    }

    this.renderQueue.add(view);

    if (!this.animationFrame) {
      this.animationFrame = requestAnimationFrame(this.renderFrame.bind(this));
    }
  }
}
